import type { TacticalFeature, TacticalTrack } from "@/services/tactical.service";

export const TACTICAL_HEATMAP_POINT_CAP = 5000;

export type BuildHeatmapPointsOptions = {
  features: TacticalFeature[];
  tracks?: TacticalTrack[];
  includeTrackHeads?: boolean;
  cap?: number;
};

function pointFeature(
  coordinates: [number, number],
  properties: Record<string, unknown>,
): GeoJSON.Feature<GeoJSON.Point> {
  return {
    type: "Feature",
    geometry: { type: "Point", coordinates },
    properties,
  };
}

/**
 * Собирает Point FeatureCollection для MapLibre heatmap:
 * kind point/text + опционально head видимых треков.
 */
export function buildTacticalHeatmapPoints(
  opts: BuildHeatmapPointsOptions,
): {
  featureCollection: GeoJSON.FeatureCollection;
  truncated: boolean;
  totalCandidates: number;
} {
  const cap = opts.cap ?? TACTICAL_HEATMAP_POINT_CAP;
  const out: GeoJSON.Feature<GeoJSON.Point>[] = [];
  let totalCandidates = 0;

  for (const f of opts.features) {
    const kind = f.properties?.kind;
    if (kind !== "point" && kind !== "text") continue;
    if (f.geometry?.type !== "Point") continue;
    const c = f.geometry.coordinates;
    if (!Number.isFinite(c[0]) || !Number.isFinite(c[1])) continue;
    totalCandidates += 1;
    if (out.length < cap) {
      out.push(
        pointFeature([Number(c[0]), Number(c[1])], {
          source: "feature",
          featureId: f.properties?.id ?? null,
          kind,
        }),
      );
    }
  }

  if (opts.includeTrackHeads && opts.tracks?.length) {
    for (const t of opts.tracks) {
      if (t.is_visible === false) continue;
      const head = t.head;
      if (!head || head.type !== "Point") continue;
      const c = head.coordinates;
      if (!Number.isFinite(c[0]) || !Number.isFinite(c[1])) continue;
      totalCandidates += 1;
      if (out.length < cap) {
        out.push(
          pointFeature([Number(c[0]), Number(c[1])], {
            source: "track",
            trackId: t.id,
          }),
        );
      }
    }
  }

  return {
    featureCollection: { type: "FeatureCollection", features: out },
    truncated: totalCandidates > out.length,
    totalCandidates,
  };
}
